/**
 * P2-04 · <ProductCard> — the one canonical product tile (P2-DB04), reused by
 * homepage blocks, PLP, search and upsells with no forks. The whole card is a
 * single accessible link to the PDP (named title + price); wishlist, quick-add
 * and the swatch buttons are real buttons layered above the anchor, so
 * activating them never navigates. Structure, state precedence (out-of-stock
 * › sale › low-stock › new — one status badge), the 4:5 grid / 1:1 list media
 * ratios, and the hover/focus model are fixed; quick-add, wishlist and
 * swatches visibility are brand flags.
 *
 * P18-19 · **This file has no `"use client"` and no hooks.** It is the card's
 * *shell*: the article, the link, the ratio boxes, the title, the layout. Every
 * part that needs state, a handler or a hook is an island — `./card-preview`
 * (media + price + the swatch state), `./card-badges` (status + rating),
 * `./swatch-row`, `./wishlist-heart`, `./quick-add-button` — and each asks its
 * own brand flag, so the shell asks none.
 *
 * So the shell renders on whichever side of the boundary its caller is on — the
 * same file, no fork — and **each caller decides by bytes, not by taste**:
 *
 * - Anything a *server* component renders is serialised a second time into the
 *   inlined RSC payload, so it costs its markup twice. Anything a *client*
 *   component renders costs its markup once, plus its props.
 * - A card is ~5.1 KB of markup built from ~1.1 KB of data, so on the surfaces
 *   that render many of them — the homepage blocks, the PLP/search grid, the
 *   PDP rails — the list is the client boundary and the shells compile into it.
 *   Rendering those shells on the server instead measured **+50 KB** on the
 *   demo homepage and **+57 KB** on a PDP.
 * - Below that boundary the ratio inverts, which is why `<CardMedia>` exists:
 *   two `next/image`s are ~3 KB of `srcset` markup built from ~180 B of URL, so
 *   they must never be the server-rendered part. They are an island
 *   *everywhere*, which is what makes a server-rendered shell safe wherever one
 *   is wanted (a one-off card on the shared-wishlist page, say).
 *
 * See `docs/storefront-performance.md` § "Where a product card's bytes go" for
 * the full decomposition and the measurements.
 */
import { FlashSaleCountdown } from "~/components/product/flash-sale-countdown";
import { LocaleLink } from "~/i18n/provider";
import { discountPercent } from "~/lib/money";
import { productHref } from "~/lib/storefront/product-href";
import { cn } from "~/lib/utils";

import { CardRating, CardStatusBadge } from "./card-badges";
import { CardMedia, CardPreviewProvider, CardPrice } from "./card-preview";
import { cardAccessibleName, deriveCardStatus, hasPriceRange } from "./derive";
import { QuickAddButton } from "./quick-add-button";
import { SwatchRow } from "./swatch-row";
import type { ProductCardView } from "./types";
import { WishlistHeart } from "./wishlist-heart";

export interface ProductCardProps {
  product: ProductCardView;
  variant?: "grid" | "list";
  /**
   * Optional reactions for a *client* caller (a toast, a count). A server
   * caller passes neither — functions cannot cross the RSC boundary, and no
   * production surface has ever needed them (see the islands' docblocks).
   */
  onQuickAdd?: () => void;
  onWishlistToggle?: (saved: boolean) => void;
  /** Above-the-fold grids pass true for the LCP candidate. */
  priority?: boolean;
  className?: string;
}

export function ProductCard({
  product,
  variant = "grid",
  onQuickAdd,
  onWishlistToggle,
  priority,
  className,
}: ProductCardProps) {
  const href = productHref(product.slug);
  const status = deriveCardStatus(product);
  const soldOut = status === "sold-out";
  const name = cardAccessibleName(product);
  // P18-25 · Card-scoped image variants: the same `sizes` breakpoints as the
  // generic `card`/`listThumb` presets, but with a `srcset` that stops where a
  // card stops. Deliberately NOT the shared presets — `card` is also worn by
  // category tiles, the mega-menu panel and promotion media, none of which are
  // bounded by a 268 px grid cell, and `listThumb` by cart lines.
  const preset = variant === "grid" ? "productCard" : "productCardThumb";
  const discountPct =
    product.priceMinMinor !== null
      ? discountPercent(product.priceMinMinor, product.compareAtMinMinor)
      : null;

  const cardLink = (
    <LocaleLink
      href={href}
      aria-label={name}
      className="absolute inset-0 z-[1] rounded-[var(--radius-lg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
    />
  );

  const media = (imageClass: string) => (
    <CardMedia
      imageUrl={product.image?.url ?? null}
      imageBlurDataUrl={product.image?.blurDataUrl}
      secondaryImageUrl={product.secondaryImage?.url}
      preset={preset}
      imageClass={imageClass}
      priority={priority}
      soldOut={soldOut}
    />
  );

  const badge = (
    <CardStatusBadge
      status={status}
      discountPct={discountPct}
      totalStock={product.totalStock}
    />
  );

  if (variant === "list") {
    return (
      <CardPreviewProvider>
        <article
          className={cn(
            "group relative grid grid-cols-[5rem_1fr_auto] items-center gap-4 rounded-[var(--radius-lg)] border border-border bg-surface p-3",
            "transition-shadow group-focus-within:shadow-md hover:shadow-md",
            className,
          )}
        >
          {cardLink}
          <div className="relative aspect-square w-20 overflow-hidden rounded-[var(--radius-md)] bg-surface-2">
            {media("object-cover")}
            <span className="absolute left-1 top-1">{badge}</span>
          </div>
          <div className={cn("min-w-0", soldOut && "opacity-70")}>
            <h3 className="truncate text-sm font-semibold text-fg">
              {product.title}
            </h3>
            <CardPrice
              priceMinor={product.priceMinMinor}
              compareAtMinor={product.compareAtMinMinor}
              currency={product.currency}
              from={hasPriceRange(product)}
              className="mt-0.5"
            />
            {product.activeSale ? (
              <FlashSaleCountdown
                sale={product.activeSale}
                className="mt-1 block text-xs font-medium text-danger tabular-nums"
              />
            ) : null}
            <CardRating
              ratingAverage={product.ratingAverage}
              ratingCount={product.ratingCount}
            />
            <SwatchRow swatches={product.swatches} href={href} className="mt-2" />
          </div>
          <div className="flex items-center gap-1">
            <WishlistHeart
              productId={product.id}
              productTitle={product.title}
              onToggle={onWishlistToggle}
            />
            <QuickAddButton
              productTitle={product.title}
              productSlug={product.slug}
              soleVariantId={product.soleVariantId}
              inStock={product.inStock}
              onQuickAdd={onQuickAdd}
              variant="list"
            />
          </div>
        </article>
      </CardPreviewProvider>
    );
  }

  return (
    <CardPreviewProvider>
      <article className={cn("group relative", className)}>
        {cardLink}
        <div
          className={cn(
            "overflow-hidden rounded-[var(--radius-lg)] border border-border bg-surface",
            "transition-[box-shadow,transform] group-focus-within:-translate-y-0.5 group-focus-within:shadow-md group-hover:-translate-y-0.5 group-hover:shadow-md",
          )}
        >
          <div className="relative aspect-[4/5] bg-surface-2">
            {media("object-cover")}
            <span className="absolute left-2 top-2">{badge}</span>
            <WishlistHeart
              productId={product.id}
              productTitle={product.title}
              onToggle={onWishlistToggle}
              className="absolute right-0.5 top-0.5"
            />
            <QuickAddButton
              productTitle={product.title}
              productSlug={product.slug}
              soleVariantId={product.soleVariantId}
              inStock={product.inStock}
              onQuickAdd={onQuickAdd}
              variant="grid"
              className="absolute inset-x-2 bottom-2"
            />
          </div>
          <div className={cn("p-3", soldOut && "opacity-70")}>
            <h3 className="line-clamp-2 text-sm font-semibold text-fg">
              {product.title}
            </h3>
            <CardPrice
              priceMinor={product.priceMinMinor}
              compareAtMinor={product.compareAtMinMinor}
              currency={product.currency}
              from={hasPriceRange(product)}
              className="mt-1"
            />
            {product.activeSale ? (
              <FlashSaleCountdown
                sale={product.activeSale}
                className="mt-1 block text-xs font-medium text-danger tabular-nums"
              />
            ) : null}
            <CardRating
              ratingAverage={product.ratingAverage}
              ratingCount={product.ratingCount}
            />
            <SwatchRow
              swatches={product.swatches}
              href={href}
              className="mt-2.5"
            />
          </div>
        </div>
      </article>
    </CardPreviewProvider>
  );
}
